import { SHIPPING_ENUM } from '@/entities/rma';
import {
  CreateReturnShippingInfo,
  PreviewShipmentModal,
  EditShipmentModal,
  CreateReturnShippingSignature,
} from '@/widgets/shipping-label-form';
import { DatePickerInput, ConfirmModal, LoadingSkeleton } from '@/shared/ui/components';
import { notiIcon } from '@/shared/ui/assets';
import { auth } from '@/shared/lib';
import { Switch } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';
import { useCreateReturnShipping } from './useCreateReturnShipping';
import CreateReturnShippingHeader from '../createReturnShippingHeader/createReturnShippingHeader';
import CreateReturnShippingRate from '../createReturnShippingRate/createReturnShippingRate';
import './createReturnShipping.scss';

/**
 * Page for creating an RMA return shipping label. Renders the missing-information confirmation, preview and sender or recipient edit modals over the state from useCreateReturnShipping, plus the label effective date, hazardous-material switch, signature options, sender and recipient info cards and the rate list, disabling the create action while data is missing or no rate is selected.
 *
 * @returns The create return shipping label page element.
 */
const CreateReturnShipping = () => {
  const {
    state,
    headerRef,
    isMissingData,
    handleClickBack,
    onChangeRate,
    onChangeShipmentDate,
    onChangeSignature,
    onChangeOptionSignature,
    onChangeCheckBox,
    toggleEditShipmentModal,
    togglePreviewShipmentModal,
    toggleMissingModal,
    onSubmitMissingModal,
    updateShipment,
  } = useCreateReturnShipping();

  const isSender = [SHIPPING_ENUM.SENDER, SHIPPING_ENUM.FIRST_EDIT_SENDER].includes(
    state.openEditShipmentModal,
  );

  return (
    <div
      ref={headerRef}
      className={classNames('create-return-shipping-page', {
        'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner(),
      })}
    >
      <ConfirmModal
        className="confirm-missing-modal"
        isOpen={state.isOpenMissingModal}
        leftButtonName={t`Cancel`}
        isBoldLeftBtn
        isFilledRightButton
        rightButtonName={t`Enter Now`}
        title={t`Missing information`}
        customContent={
          <div className="custom-content">
            {t`The following required information is missing:`}
            <ul aria-label={t`Missing information`}>
              {_.map(state.missingModalData, (item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            {t`Please select “Enter Now” to review and fill in the missing information.`}
          </div>
        }
        isSmallSize
        onClickLeftButton={toggleMissingModal}
        onClickRightButton={onSubmitMissingModal}
      />
      <PreviewShipmentModal
        data={state}
        onCancel={togglePreviewShipmentModal}
        open={state.isOpenPreviewShipmentModal}
        width={900}
      />
      <EditShipmentModal
        open={Boolean(state.openEditShipmentModal)}
        isSender={isSender}
        onCancel={toggleEditShipmentModal}
        data={isSender ? state.senderContact : state.recipientContact}
        title={isSender ? t`Edit sender information` : t`Edit recipient information`}
        listCountries={state.listCountries}
        listStatesCA={state.listStatesCA}
        listStatesUS={state.listStatesUS}
        updateData={updateShipment}
      />

      <CreateReturnShippingHeader
        title={t`Create return shipping label`}
        isLoadedData={state.isLoadedData}
        isDisable={isMissingData || !state.rateId}
        onClickCreate={togglePreviewShipmentModal}
        handleClickBack={handleClickBack}
      />

      <div className="create-return-shipping__body">
        <div className="create-return-shipping__left">
          <div className="box">
            <div className="box__header">{t`Packet information`}</div>
            <div className="box__body">
              <div className="input-date">
                <LoadingSkeleton isLoading={!state.isLoadedData}>
                  <DatePickerInput
                    name="shipmentDate"
                    placeholder="MM/DD/YYYY"
                    value={state.shipmentDate}
                    belowLimit={dayjs().startOf('day').valueOf()}
                    onChange={onChangeShipmentDate}
                    label={t`Label effective date`}
                    className="shipment-date"
                  />
                  <div className="shipment-warning">
                    <img src={notiIcon} alt="icon noti" />
                    {t`Please note that the purchased label is still effective 30 days after the selected date.`}
                  </div>
                  <div className="swich-checkbox">
                    <div className="swich-checkbox__header">{t`Hazardous materials`}</div>
                    <div className="swich-checkbox__checkbox">
                      <Switch
                        id="hazardous-materials-toggle"
                        {...({ width: 34, height: 18 } as any)}
                        checked={state.dangerousGoods}
                        onChange={() => onChangeCheckBox('dangerousGoods', !state.dangerousGoods)}
                        aria-label={t`Contains hazardous materials`}
                      />
                      <div className="swich-checkbox__label">{t`Contains hazardous materials`}</div>
                    </div>
                  </div>
                  <CreateReturnShippingSignature
                    signatureConfirmation={state.signatureConfirmation}
                    onChangeOptionSignature={onChangeOptionSignature}
                    onChangeSignature={onChangeSignature}
                  />
                </LoadingSkeleton>
              </div>
            </div>
          </div>

          <div className="shipping">
            <div className="shipping_sender">
              <LoadingSkeleton isLoading={!state.isLoadedData}>
                <CreateReturnShippingInfo
                  data={state.senderContact}
                  listStatesCA={state.listStatesCA}
                  listStatesUS={state.listStatesUS}
                  isSender
                  onEdit={() => {
                    toggleEditShipmentModal(SHIPPING_ENUM.SENDER);
                  }}
                />
              </LoadingSkeleton>
              <div />
            </div>
            <div className="shipping_recipient">
              <LoadingSkeleton isLoading={!state.isLoadedData}>
                <CreateReturnShippingInfo
                  data={state.recipientContact}
                  listStatesCA={state.listStatesCA}
                  listStatesUS={state.listStatesUS}
                  onEdit={() => toggleEditShipmentModal(SHIPPING_ENUM.RECIPIENT)}
                />
              </LoadingSkeleton>
            </div>
          </div>
        </div>

        <div className="create-return-shipping__right">
          <CreateReturnShippingRate
            isReturnShippingLabel
            isLoadedData={state.isLoadedData && state.isLoadedRate}
            isMissingData={isMissingData}
            rates={state.rates}
            rateId={state.rateId}
            onChange={onChangeRate}
          />
        </div>
      </div>
    </div>
  );
};

export default CreateReturnShipping;
